import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Plus, X, MessageCircle, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtEur, errMsg, waPhone } from "@/lib/api";
import { PptoCondiciones, CONDICIONES, condicionesPorLineas } from "@/components/PptoCondiciones";
import { PlantillasEmail, ProgramarEnvio, fmtFechaHora } from "@/components/EmailHerramientas";
import { ProgramarWa } from "@/components/WaProgramados";

import { makePdf as makePdfFile, toB64 } from "@/lib/pdf";

const makePdf = (els, nombre) => makePdfFile(els, `Presupuesto ${nombre}.pdf`);

const iniLines = (sug, tarifas) => {
  const out = [];
  sug.forEach((s) => {
    const t = tarifas.find((x) => (x.sug || "").split(",").includes(s));
    if (t && !out.some((l) => l.concepto === t.nombre)) out.push({ concepto: t.nombre, cantidad: s === "tomatis" ? 3 : 1, precio: t.precio });
  });
  return out;
};

const lineTotal = (l) => Number(l.cantidad || 0) * Number(l.precio || 0) * (1 - Number(l.dcto || 0) / 100);
const calc = (lines, pu, dx = 0) => {
  const sub = lines.reduce((s, l) => s + lineTotal(l), 0);
  const desc = pu ? Math.round(sub * 5) / 100 : 0;
  const descX = Math.round(sub * Number(dx || 0)) / 100;
  return { sub, desc, descX, total: sub - desc - descX };
};

const Cell = ({ value, onChange, num, testId, show }) => (
  <>
    <input type={num ? "number" : "text"} className={`no-pdf w-full bg-transparent outline-none ${num ? "text-right" : ""}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    <span className={`pdf-only ${num ? "text-right" : ""}`}>{show ?? value}</span>
  </>
);

const numTxt = (v) => Number(v || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });
const pct = (v) => Math.min(Math.max(Number(v), 0), 100);

const Line = ({ l, i, onChange, onDel }) => (
  <tr className="border-b border-slate-200" data-testid={`ppto-line-${i}`}>
    <td className="py-1.5 pr-2"><Cell value={l.concepto} onChange={(v) => onChange({ ...l, concepto: v })} testId={`ppto-concepto-${i}`} /></td>
    <td className="py-1.5 w-16"><Cell num value={l.cantidad} onChange={(v) => onChange({ ...l, cantidad: v })} testId={`ppto-cantidad-${i}`} show={numTxt(l.cantidad)} /></td>
    <td className="py-1.5 w-24"><Cell num value={l.precio} onChange={(v) => onChange({ ...l, precio: v })} testId={`ppto-precio-${i}`} show={fmtEur(l.precio)} /></td>
    <td className="py-1.5 w-16"><Cell num value={l.dcto ?? 0} onChange={(v) => onChange({ ...l, dcto: v === "" ? "" : pct(v) })} testId={`ppto-dcto-${i}`} show={`${numTxt(l.dcto)} %`} /></td>
    <td className="py-1.5 w-28 text-right font-medium">{fmtEur(lineTotal(l))}</td>
    <td className="w-6 no-print"><button onClick={onDel} data-testid={`ppto-del-${i}`} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button></td>
  </tr>
);

const Header = ({ c }) => (
  <div className="text-center border-b-2 border-[#2F6B4F] pb-4">
    <img src="/logo-sv.jpg" alt="Salud y Visión" className="h-20 mx-auto" data-testid="ppto-logo" />
    <div className="font-[Outfit] text-3xl font-bold text-[#2F6B4F]">Salud y Visión</div>
    <div className="text-xs tracking-[0.2em] uppercase text-[#2F6B4F]">Optometría y Terapia Visual</div>
    <div className="text-[11px] text-slate-600 mt-2">{c.razon_social} · CIF {c.cif}</div>
    {(c.centros || []).map((x) => <div key={x} className="text-[11px] text-slate-600">{x}</div>)}
    <div className="text-[11px] text-[#2F6B4F]">{c.email} · {c.web}</div>
  </div>
);

export default function PresupuestoImprimir() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tar, setTar] = useState(null);
  const [lines, setLines] = useState([]);
  const [notas, setNotas] = useState("El kit se cobra aparte del bono, el primer día de terapia y una sola vez.");
  const [add, setAdd] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState({});
  const [sp] = useSearchParams();
  const [bid, setBid] = useState(sp.get("id") || "");
  const [estado, setEstado] = useState("HECHO");
  const [cond, setCond] = useState(null);
  const [pu, setPu] = useState(false);
  const [dx, setDx] = useState("");
  const [dxTxt, setDxTxt] = useState("");
  const [gmail, setGmail] = useState(null);
  const [dest, setDest] = useState(null);
  const [emailAt, setEmailAt] = useState("");
  const [mailAbierto, setMailAbierto] = useState(false);
  const [borradorAt, setBorradorAt] = useState("");
  const [prog, setProg] = useState({});
  const [waFecha, setWaFecha] = useState("");
  const setProgDe = (d) => setProg({ at: d.email_programado_at || "", a: d.email_programado_a || "", error: d.email_programado_error || "" });
  const cancelarProg = async () => {
    try {
      const { data } = await api.delete(`/presupuestos/${bid}/email-programado`);
      setProgDe(data);
      toast.success("Envío programado cancelado");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const marcarEnviado = async () => {
    try {
      await guardar("ENVIADO", false);
      setEmailAt(new Date().toISOString());
      toast.success("Presupuesto marcado como enviado");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    api.get("/gmail/status").then((r) => setGmail(r.data));
  }, []);
  const ref = useRef(null);
  const ref2 = useRef(null);
  const guardar = async (est = estado, aviso = true) => {
    const body = { patient_id: id, lineas: lines, total: calc(lines, pu, dx).total, pago_unico: pu, dcto_extra: Number(dx || 0), dcto_extra_txt: dxTxt, notas, estado: est, condiciones: condTipo };
    const { data } = bid ? await api.put(`/presupuestos/${bid}`, body) : await api.post("/presupuestos", body);
    setBid(data.id);
    setEstado(data.estado);
    if (est === "RECHAZADO" && estado !== "RECHAZADO") toast.info("Ficha marcada como No aprobado");
    if (data.created_treatments?.length) toast.success(`Tratamientos creados: ${data.created_treatments.join(", ")}`);
    if (aviso) toast.success("Presupuesto guardado");
    return data;
  };
  useEffect(() => {
    Promise.all([api.get(`/patients/${id}`), api.get("/tarifas"), sp.get("id") ? api.get(`/presupuestos/${sp.get("id")}`) : Promise.resolve(null)]).then(([a, b, c]) => {
      setP(a.data.patient);
      setTar(b.data);
      if (c) {
        setLines(c.data.lineas);
        setNotas(c.data.notas);
        setEstado(c.data.estado);
        if (c.data.condiciones !== undefined) setCond(c.data.condiciones);
        setPu(!!c.data.pago_unico);
        setDx(c.data.dcto_extra ? String(c.data.dcto_extra) : "");
        setDxTxt(c.data.dcto_extra_txt || "");
        setEmailAt(c.data.email_enviado_at || "");
        setBorradorAt(c.data.email_borrador_at || "");
        setProgDe(c.data);
        setWaFecha(c.data.wa_programado_fecha || "");
      } else setLines(iniLines(a.data.patient.tratamientos_sugeridos || [], b.data.tarifas));
    });
  }, [id, sp]);
  useEffect(() => {
    if (sp.get("pdf") !== "1" || !p || !tar) return;
    const send = (m) => window.parent.postMessage({ type: "ppto-pdf", id: sp.get("id"), ...m }, window.location.origin);
    const t = setTimeout(async () => {
      try {
        const file = await makePdf([ref.current, ref2.current], p.nombre);
        send({ b64: await toB64(file), filename: file.name });
      } catch (e) {
        send({ error: String(e) });
      }
    }, 800);
    return () => clearTimeout(t);
  }, [p, tar, sp]);
  const condTipo = cond ?? condicionesPorLineas(lines);
  if (!p || !tar) return <div className="p-8 text-slate-400">Cargando…</div>;
  const { sub, desc, descX, total } = calc(lines, pu, dx);
  const addLine = (tid) => {
    const t = tar.tarifas.find((x) => x.id === tid);
    setLines([...lines, t ? { concepto: t.nombre, cantidad: 1, precio: t.precio } : { concepto: "", cantidad: 1, precio: 0 }]);
    setAdd("");
  };
  const totalTxt = fmtEur(total);
  const msgWa = msgs.wa ?? `Hola, te enviamos el presupuesto de Salud y Visión para ${p.nombre} con los tratamientos que te propusimos en la evaluación. Total: ${totalTxt}.\n\nSi tienes cualquier duda o quieres confirmarlo, escríbenos o llámanos.\n\nUn saludo,\nSalud y Visión`;
  const msgAsunto = msgs.asunto ?? `Presupuesto Salud y Visión – ${p.nombre}`;
  const msgMail = msgs.mail ?? `Hola:\n\nTe adjuntamos el presupuesto de los tratamientos que te propusimos en la evaluación de ${p.nombre}.\n\nTotal: ${totalTxt}\n\nSi tienes cualquier duda o quieres confirmarlo, puedes responder a este correo o llamarnos:\nMadrid 91 404 48 77 · Alcobendas 91 519 87 87 · 657 95 78 21\n\nUn saludo,\nSalud y Visión\n${tar.clinica.web || ""}`;
  const para = dest ?? (p.email || "");
  const msgAviso = msgs.aviso ?? `Hola, te acabamos de enviar por correo electrónico${para ? ` (${para})` : ""} el presupuesto de Salud y Visión para ${p.nombre}. Revísalo cuando puedas y, si tienes cualquier duda, escríbenos por aquí o llámanos.\n\nUn saludo,\nSalud y Visión`;
  const avisarWa = () => window.open(`https://wa.me/${waPhone(p.telefono)}?text=${encodeURIComponent(msgAviso)}`, "_blank");
  const enviarGmail = async (enviarAt = "") => {
    if (!para) return toast.error("Escribe el email del paciente en el apartado de mensajes");
    setBusy(true);
    try {
      const saved = await guardar(estado, false);
      const file = await makePdf([ref.current, ref2.current], p.nombre);
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
      const { data } = await api.post(`/presupuestos/${saved.id}/enviar-email`, { to: para, asunto: msgAsunto, cuerpo: msgMail, pdf_base64: b64, filename: file.name, modo: enviarAt ? "programar" : "borrador", enviar_at: enviarAt });
      setProgDe(data);
      if (enviarAt) return toast.success(`Correo programado para el ${fmtFechaHora(data.email_programado_at)}`, { duration: 8000 });
      setBorradorAt(data.email_borrador_at);
      toast.success(`Borrador guardado en Gmail (${gmail.email || "cuenta del centro"}). Ábrelo en Borradores para enviarlo o programarlo.`, { duration: 8000 });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const enviar = async (canal) => {
    setBusy(true);
    try {
      await guardar(estado === "HECHO" ? "ENVIADO" : estado, false);
      const file = await makePdf([ref.current, ref2.current], p.nombre);
      const texto = canal === "wa" ? msgWa : msgMail;
      if (canal === "mail") setMailAbierto(true);
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: texto, title: msgAsunto });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(file);
        a.download = file.name;
        a.click();
        if (canal === "wa") window.open(`https://wa.me/${waPhone(p.telefono)}?text=${encodeURIComponent(texto)}`, "_blank");
        else window.location.href = `mailto:${para}?subject=${encodeURIComponent(msgAsunto)}&body=${encodeURIComponent(texto)}`;
        toast.info(`PDF descargado. En el ${canal === "wa" ? "WhatsApp" : "correo"} pulsa el clip y adjunta el archivo descargado.`, { duration: 10000 });
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("No se pudo preparar el PDF");
    }
    setBusy(false);
  };
  const enviarWa = () => enviar("wa");
  const programarWa = async (fecha) => {
    if (!p.telefono) return toast.error("La ficha no tiene teléfono");
    setBusy(true);
    try {
      const saved = await guardar(estado, false);
      const file = await makePdf([ref.current, ref2.current], p.nombre);
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
      const { data } = await api.post(`/presupuestos/${saved.id}/wa-programado`, { fecha, texto: msgWa, pdf_base64: b64, filename: file.name });
      setWaFecha(data.wa_programado_fecha);
      toast.success(`Aparecerá en el Panel principal el ${fmtDate(fecha)} para enviarlo`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const cancelarWa = async () => {
    try {
      await api.delete(`/presupuestos/${bid}/wa-programado`);
      setWaFecha("");
      toast.success("Quitado de la lista");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white min-h-screen" data-testid="presupuesto-page">
      <div className="max-w-[800px] mx-auto p-8" ref={ref}>
        <div className="flex flex-col gap-2 no-print mb-4">
          <span className="text-sm text-slate-500">Puedes cambiar conceptos, cantidades y precios antes de imprimir.</span>
          <div className="flex flex-wrap gap-2">
            <span data-testid="ppto-estado" className="self-center text-xs rounded bg-slate-100 px-2 py-1 text-slate-600">{bid ? `Guardado · ${estado}` : "Sin guardar"}</span>
            <button data-testid="ppto-save-btn" onClick={() => guardar()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm"><Save className="h-4 w-4" />Guardar</button>
            <button data-testid="ppto-wa-btn" onClick={enviarWa} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-[#25D366] text-white px-4 py-2 text-sm disabled:opacity-60"><MessageCircle className="h-4 w-4" />{busy ? "Preparando…" : "Enviar por WhatsApp"}</button>
            <button data-testid="ppto-mail-btn" onClick={() => (gmail?.conectado ? enviarGmail() : enviar("mail"))} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-[#1E3A8A] text-white px-4 py-2 text-sm disabled:opacity-60"><Mail className="h-4 w-4" />{gmail?.conectado ? "Dejar en borradores de Gmail" : "Enviar por correo"}</button>
            <select data-testid="ppto-condiciones-select" className="field-select h-9 text-sm py-0" style={{ width: "auto" }} value={condTipo} onChange={(e) => setCond(e.target.value)} title="Condiciones de la segunda hoja">
              {Object.entries(CONDICIONES).map(([k, c]) => <option key={k} value={k}>Condiciones: {c.label}</option>)}
              <option value="">Sin hoja de condiciones</option>
            </select>
            <button data-testid="ppto-print-btn" onClick={async () => { await guardar(estado, false); window.print(); }} className="inline-flex items-center gap-2 rounded-md bg-[#2F6B4F] text-white px-4 py-2 text-sm"><Printer className="h-4 w-4" />Imprimir / PDF</button>
          </div>
          {prog.at && (
            <div data-testid="ppto-programado-banner" className="flex flex-wrap items-center gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
              <span>Correo programado para el <b>{fmtFechaHora(prog.at)}</b> a {prog.a}. Saldrá desde el Gmail del centro (puede tardar hasta 15 minutos).</span>
              <button onClick={cancelarProg} data-testid="ppto-cancelar-programado" className="rounded-md border border-indigo-300 bg-white px-3 py-1.5">Cancelar envío</button>
            </div>
          )}
          {prog.error && !prog.at && <div data-testid="ppto-programado-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">No se pudo enviar el correo programado: {prog.error}. Revisa la conexión de Gmail en Equipo y vuelve a programarlo.</div>}
          {borradorAt && estado === "HECHO" && (
            <div data-testid="ppto-borrador-banner" className="flex flex-wrap items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <span>Borrador guardado en Gmail el {fmtDate(borradorAt)}. Ábrelo y pulsa la flecha junto a <b>Enviar</b> → <b>Programar envío</b>.</span>
              <a href="https://mail.google.com/mail/#drafts" target="_blank" rel="noreferrer" data-testid="ppto-abrir-borradores" className="rounded-md bg-[#1E3A8A] text-white px-3 py-1.5">Abrir borradores</a>
              <button onClick={marcarEnviado} data-testid="ppto-marcar-enviado" className="rounded-md border border-blue-300 bg-white px-3 py-1.5">Ya está enviado o programado</button>
            </div>
          )}
          {(emailAt || mailAbierto) && (
            <div data-testid="ppto-aviso-banner" className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              <span>{emailAt ? `Correo enviado el ${fmtDate(emailAt)}. Avísale por WhatsApp de que lo tiene en su correo:` : "Cuando hayas enviado el correo, avísale por WhatsApp:"}</span>
              <button data-testid="ppto-aviso-wa-btn" onClick={avisarWa} className="inline-flex items-center gap-2 rounded-md bg-[#25D366] text-white px-3 py-1.5 text-sm"><MessageCircle className="h-4 w-4" />Avisar por WhatsApp</button>
            </div>
          )}
          {gmail && !gmail.conectado && <span className="text-[11px] text-slate-400" data-testid="ppto-gmail-off">El Gmail del centro no está conectado: el correo se abrirá en tu programa de correo. Conéctalo en Equipo.</span>}
        </div>
        <Header c={tar.clinica} />
        <h1 className="font-[Outfit] text-2xl font-bold text-[#2F6B4F] text-center mt-6">Presupuesto</h1>
        <div className="mt-4 bg-[#EEF5F0] rounded-md p-4 text-sm flex flex-wrap justify-between gap-2">
          <div><b className="text-[#2F6B4F]">{p.nombre}</b>{p.codigo ? ` · ${p.codigo}` : ""}<div className="text-slate-600">{p.centro}{p.telefono ? ` · ${p.telefono}` : ""}</div></div>
          <div className="text-right text-slate-600">Fecha: {fmtDate(new Date().toISOString())}{p.fecha_ee ? <div>Evaluación: {fmtDate(p.fecha_ee)}</div> : null}</div>
        </div>
        <table className="w-full text-sm mt-6" data-testid="ppto-table">
          <thead><tr className="border-b-2 border-[#2F6B4F] text-[11px] uppercase tracking-wider text-[#2F6B4F]"><th className="text-left py-1">Concepto</th><th className="text-right">Cant.</th><th className="text-right">Precio</th><th className="text-right">% Dcto</th><th className="text-right">Importe</th><th className="no-print" /></tr></thead>
          <tbody>{lines.map((l, i) => <Line key={i} l={l} i={i} onChange={(nl) => setLines(lines.map((x, j) => (j === i ? nl : x)))} onDel={() => setLines(lines.filter((_, j) => j !== i))} />)}</tbody>
          <tfoot>{(pu || descX > 0) && <>
            <tr><td colSpan={4} className="pt-3 text-right text-slate-600">Subtotal</td><td className="pt-3 text-right" data-testid="ppto-subtotal">{fmtEur(sub)}</td><td /></tr>
            {pu && <tr><td colSpan={4} className="pt-1 text-right text-slate-600">Descuento pago único (5 %)</td><td className="pt-1 text-right text-emerald-700" data-testid="ppto-desc-pu">−{fmtEur(desc)}</td><td /></tr>}
            {descX > 0 && <tr><td colSpan={4} className="pt-1 text-right text-slate-600">{dxTxt || "Descuento"} ({Number(dx)} %)</td><td className="pt-1 text-right text-emerald-700" data-testid="ppto-desc-extra">−{fmtEur(descX)}</td><td /></tr>}
          </>}<tr><td colSpan={4} className="pt-3 text-right font-semibold">Total</td><td className="pt-3 text-right font-[Outfit] text-lg font-bold text-[#2F6B4F]" data-testid="ppto-total">{fmtEur(total)}</td><td /></tr></tfoot>
        </table>
        {lines.length === 0 && <p className="text-sm text-slate-400 mt-2 no-print" data-testid="ppto-empty">No hay tratamientos propuestos con precio. Añade conceptos abajo.</p>}
        <div className="no-print flex gap-2 mt-3">
          <select className="field-select flex-1" value={add} onChange={(e) => setAdd(e.target.value)} data-testid="ppto-add-select">
            <option value="">Añadir concepto de la tabla de precios…</option>
            {tar.tarifas.map((t) => <option key={t.id} value={t.id}>{t.nombre} · {fmtEur(t.precio)}</option>)}
          </select>
          <button onClick={() => addLine(add)} data-testid="ppto-add-btn" className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 text-sm"><Plus className="h-4 w-4" />{add ? "Añadir" : "Línea libre"}</button>
          <button onClick={() => setPu(!pu)} data-testid="ppto-pago-unico-btn" className={`rounded-md border px-3 text-sm whitespace-nowrap ${pu ? "bg-emerald-600 border-emerald-600 text-white" : "border-emerald-600 text-emerald-700"}`}>{pu ? "Quitar 5 % pago único" : "Aplicar 5 % pago único"}</button>
          <div className="flex items-center gap-1 rounded-md border border-emerald-600 px-2 text-sm text-emerald-700" data-testid="ppto-dcto-extra-box">
            <input className="w-32 bg-transparent outline-none text-slate-700" placeholder="Otro descuento" value={dxTxt} onChange={(e) => setDxTxt(e.target.value)} data-testid="ppto-dcto-extra-txt" />
            <input type="number" min="0" max="100" className="w-12 text-right bg-transparent outline-none text-slate-700" placeholder="0" value={dx} onChange={(e) => setDx(e.target.value === "" ? "" : String(Math.min(Math.max(Number(e.target.value), 0), 100)))} data-testid="ppto-dcto-extra" />%
          </div>
        </div>
        <textarea className="no-pdf w-full mt-6 text-xs text-slate-600 border border-slate-200 rounded p-2 print:border-0" rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} data-testid="ppto-notas" />
        {notas && <p className="pdf-only mt-6 text-xs text-slate-600 whitespace-pre-line">{notas}</p>}
        {tar.clinica.pie && <p className="text-[11px] text-slate-500 mt-2">{tar.clinica.pie}</p>}
      </div>
      {condTipo && <div className="max-w-[800px] mx-auto px-8 pb-8 pt-6 break-before-page border-t border-dashed border-slate-300 print:border-0" ref={ref2}><PptoCondiciones tipo={condTipo} /></div>}
      <div className="max-w-[800px] mx-auto px-8 pb-10 no-print" data-testid="ppto-mensajes">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Mensajes que se enviarán (puedes cambiarlos)</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-[11px] text-slate-500">WhatsApp{p.telefono ? ` · ${p.telefono}` : " · sin teléfono en la ficha"}
            <PlantillasEmail canal="wa" cuerpo={msgWa} vars={{ paciente: p.nombre, total: totalTxt }} onApply={(_, wa) => setMsgs({ ...msgs, wa })} />
            <textarea rows={9} className="w-full mt-1 text-sm border border-slate-200 rounded p-2" value={msgWa} onChange={(e) => setMsgs({ ...msgs, wa: e.target.value })} data-testid="ppto-msg-wa" />
            <ProgramarWa busy={busy} fecha={waFecha} onProgramar={programarWa} onCancelar={cancelarWa} />
          </label>
          <div className="text-[11px] text-slate-500">Correo{p.email ? "" : " · sin email en la ficha"}
            <input className="w-full mt-1 h-9 text-sm border border-slate-200 rounded px-2" placeholder="Email del paciente" value={para} onChange={(e) => setDest(e.target.value)} data-testid="ppto-mail-to" />
            <PlantillasEmail asunto={msgAsunto} cuerpo={msgMail} vars={{ paciente: p.nombre, total: totalTxt }} onApply={(asunto, mail) => setMsgs({ ...msgs, asunto, mail })} />
            <input className="w-full mt-1 h-9 text-sm border border-slate-200 rounded px-2" value={msgAsunto} onChange={(e) => setMsgs({ ...msgs, asunto: e.target.value })} data-testid="ppto-msg-asunto" />
            <textarea rows={7} className="w-full mt-1 text-sm border border-slate-200 rounded p-2" value={msgMail} onChange={(e) => setMsgs({ ...msgs, mail: e.target.value })} data-testid="ppto-msg-mail" />
            {gmail?.conectado && <ProgramarEnvio busy={busy} onProgramar={enviarGmail} />}
          </div>
          <label className="text-[11px] text-slate-500 md:col-span-2">WhatsApp de aviso tras enviar el correo
            <textarea rows={3} className="w-full mt-1 text-sm border border-slate-200 rounded p-2" value={msgAviso} onChange={(e) => setMsgs({ ...msgs, aviso: e.target.value })} data-testid="ppto-msg-aviso" />
          </label>
        </div>
      </div>
    </div>
  );
}
